import BaseHtmlElement from "./ui/base-html-element.ts";
import WebsocketWrapper from "./websocket-wrapper.ts";
import Route from "./enums/route.ts";
import BaseUI from "./ui/base-ui.ts";
import ConnectingScreen from "./ui/screens/connecting.ts";
import MainMenuScreen from "./ui/screens/main-menu.ts";
import CreateGroupLobbyScreen from "./ui/screens/create-group-lobby.ts";
import JoinGroupScreen from "./ui/screens/join-group.ts";
import RestaurantCategoriesScreen from "./ui/screens/restaurant-categories.ts";
import TopRestaurantsScreen from "./ui/screens/top-restaurants.ts";
import FinalResultScreen from "./ui/screens/final-result.ts";
import ChooseNicknameScreen from "./ui/screens/choose-nickname.ts";
import State from "./state/state.ts";
import EatOutApiClient from "./api-client/eat-out-api-client.ts";
import PopUpNotification from "./ui/pop-up-notification.ts";
import NotificationType from "./enums/notification-type.ts";
import LogUtils from "./utils/log-utils.ts";
import RestaurantCategoriesLobbyScreen from "./ui/screens/restaurant-categories-lobby.ts";
import TopRestaurantsLobbyScreen from "./ui/screens/top-restaurants-lobby.ts";

export let popUpNotificationContainer: HTMLDivElement | null = null;

export default class EatOutApp extends BaseHtmlElement {
  private readonly ws: WebsocketWrapper;
  private readonly apiClient: EatOutApiClient;
  private screen: BaseUI | null = null;
  private route: Route | null = null;
  private state: State = new State();

  constructor() {
    super("eat-out-app");

    // generate the websocket
    const wsUrl = `${
      globalThis.location.protocol.startsWith("https") ? "wss" : "ws"
    }://${globalThis.location.host}/api/v1/ws`;
    this.ws = new WebsocketWrapper(this.debug, wsUrl);
    this.ws.attemptConnectionCallback = this.handleWebsocketConnectionAttempt.bind(this);
    this.ws.successfullyConnectedCallback = this.handleNewWebsocketConnection.bind(this);
    this.ws.startReconnectionLoopCallback = this.handleWebsocketDisconnection.bind(this);
    this.ws.reconnectionSuccessfulCallback = this.handleWebsocketReconnection.bind(this);

    // create the higher-order ApiClient, which is a super-type of the WebsocketWrapper
    this.apiClient = new EatOutApiClient(this.debug, this.ws, this.state);

    // export the pop-up notification container that is attached to this web component's shadow dom root
    popUpNotificationContainer = this.popUpNotificationContainer;
  }

  override connectedCallback(): void {
    super.connectedCallback();

    document.addEventListener("DOMContentLoaded", () => {
      // connect the websocket
      this.ws.connect();
    });
  }

  /**
   * When the websocket finally establishes a connection, checks to see if the user has credentials stored. If so,
   * automatically attempts logging the user into their account. If not, sends the user to the ChooseNickname screen.
   * @private
   */
  private handleNewWebsocketConnection(): void {
    const nickname: string | null = this.state.userState.name;

    // nickname not choosen yet, make them pick one out
    if (nickname === null) {
      LogUtils.debug(this.debug, "nickname not chosen yet - sending to choose-nickname screen");
      this.navigate(Route.CHOOSE_NICKNAME);
      return;
    }

    // nickname exists, so let's attempt logging in
    LogUtils.debug(this.debug, `nickname already set: '${nickname}' - logging in`);
    this.apiClient.sendLoginGuestUserPayload(nickname);
  }

  /**
   * Redirects to the Loading screen when the websocket connection is lost.
   * @private
   */
  private handleWebsocketDisconnection(): void {
    new PopUpNotification(NotificationType.ERROR, "Connection lost. Reconnecting...", true);
    this.navigate(Route.CONNECTING);
  }

  /**
   * Notifies the user that the websocket has reconnected.
   * @private
   */
  private handleWebsocketReconnection(): void {
    new PopUpNotification(NotificationType.INFO, "Reconnected");
  }

  /**
   * TODO
   * @private
   */
  private handleWebsocketConnectionAttempt(): void {
    this.navigate(Route.CONNECTING);
  }

  /**
   * Navigates to the given screen route.
   * @param newRoute The new route in which to switch to.
   * @private
   */
  private navigate(newRoute: Route): void {
    // only change screen route if it's actually different
    if (newRoute === this.route) {
      LogUtils.warn(this.debug, `new state matches current state, ${newRoute}; not switching`);
      return;
    }

    // set the new screen route
    this.route = newRoute;

    // re-render the app screen
    this.render();
  }

  override render(newElements: HTMLElement[] = []): void {
    // if `route` is null, then the app hasn't been initialized yet
    if (this.route === null) {
      LogUtils.error("attempted to render but `route` hasn't been initialized yet");
      return;
    }

    // set current screen to null in order to force-wipe it
    this.screen = null;

    // reset all API client callbacks
    this.apiClient.resetCallbacks();

    // render the proper new screen, given the current state
    switch (this.route) {
      case Route.CONNECTING: {
        this.screen = new ConnectingScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.CHOOSE_NICKNAME: {
        this.screen = new ChooseNicknameScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.MAIN_MENU: {
        this.screen = new MainMenuScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.CREATE_GROUP_LOBBY: {
        this.screen = new CreateGroupLobbyScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.JOIN_GROUP: {
        this.screen = new JoinGroupScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.RESTAURANT_CATEGORIES: {
        this.screen = new RestaurantCategoriesScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.RESTAURANT_CATEGORIES_LOBBY: {
        this.screen = new RestaurantCategoriesLobbyScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.TOP_CHOICES: {
        this.screen = new TopRestaurantsScreen(
          this.state,
          this.apiClient,
          this.navigate.bind(this),
          this.shadowRealm,
        );
        break;
      }

      case Route.TOP_RESTAURANTS_LOBBY: {
        this.screen = new TopRestaurantsLobbyScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      case Route.FINAL_RESULT: {
        this.screen = new FinalResultScreen(this.state, this.apiClient, this.navigate.bind(this));
        break;
      }

      default: {
        LogUtils.error(`attempted to render an unknown screen route: ${this.route}`);
      }
    }

    // double-check that current screen exists
    if (this.screen === null) {
      LogUtils.error("attempted to render, but `screen` was null");
    }

    // replace everything on the app screen with the new elements
    newElements.push(this.screen.render());
    super.render(newElements);
  }
}
